import React, {useEffect, useState} from 'react'
import {ListItemIcon, ListItemText, Menu, MenuItem} from '@mui/material'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import LogoutIcon from '@mui/icons-material/Logout'
import {Link as RouterLink, useHistory} from 'react-router-dom'
import {FormattedMessage} from 'react-intl'
import {useCookies} from 'react-cookie'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import {dispatch} from 'src/redux-store'
import {clearPspConfig} from 'src/slices/pspConfigSlice'
import {clearUserInfo} from 'src/slices/userSlice'
import {PATHS, STATUS} from 'src/constants/constants'
import {NEW_PATHS} from 'src/pages-new/mainRouting/routesMap'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {useHasMultiplePrograms} from 'src/utils/useHasMultiplePrograms'
import UserChip from './UserChip'

interface UserMenuProps {
  expanded?: boolean
}

const UserMenu = ({expanded = true}: UserMenuProps): JSX.Element => {
  const headerConfiguration = useAppSelector(state => state.pspConfigReducer.data.headerConfiguration ?? {})
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [anchorWidth, setAnchorWidth] = useState<number>()
  const [logoutStatus, setLogoutStatus] = useState<string>()
  const toast = useToast()
  const [, , removeCookie] = useCookies(['sid', 'tokens'])
  const history = useHistory()

  const open = Boolean(anchorEl)

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    setAnchorWidth(e.currentTarget.offsetWidth)
  }
  const handleClose = () => setAnchorEl(null)

  const onLogout = () => {
    handleClose()
    apiClient.logout(setLogoutStatus)
  }

  useEffect(() => {
    if (logoutStatus === STATUS.FULFILLED || logoutStatus === STATUS.REJECTED) {
      removeCookie('sid')
      removeCookie('tokens')
      dispatch(clearPspConfig())
      dispatch(clearUserInfo())
      history.push(PATHS.LOGIN)
    }
  }, [logoutStatus, removeCookie, history, toast])

  const showProfile = Boolean(headerConfiguration.profile)
  const hasMultiplePrograms = useHasMultiplePrograms(Boolean(headerConfiguration.changePortal))
  const showChangePortal = Boolean(headerConfiguration.changePortal) && hasMultiplePrograms
  const showLogout = Boolean(headerConfiguration.logout)
  const hasItems = showProfile || showChangePortal || showLogout

  return (
    <>
      <UserChip
        expanded={expanded}
        onClick={hasItems ? handleOpen : undefined}
        showChevron={hasItems}
        active={open}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            width: anchorWidth,
            minWidth: anchorWidth ? Math.max(anchorWidth, 180) : 200,
            borderRadius: 1.5,
          },
        }}
        MenuListProps={{
          sx: {
            py: 0.5,
            '& .MuiMenuItem-root': {
              minHeight: 45,
              px: 2,
              py: 0,
            },
            '& .MuiListItemIcon-root': {
              minWidth: 40,
            },
          },
        }}
      >
        {showProfile && (
          <MenuItem
            component={RouterLink}
            to={PATHS.PROFILE}
            onClick={handleClose}
          >
            <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primaryTypographyProps={{fontSize: 14}}
              primary={<FormattedMessage id="profile" />}
            />
          </MenuItem>
        )}
        {showChangePortal && (
          <MenuItem
            component={RouterLink}
            to={NEW_PATHS.PORTALS}
            onClick={handleClose}
          >
            <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primaryTypographyProps={{fontSize: 14}}
              primary={<FormattedMessage id="cambiaPortale" />}
            />
          </MenuItem>
        )}
        {showLogout && (
          <MenuItem onClick={onLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primaryTypographyProps={{fontSize: 14}}
              primary={<FormattedMessage id="logout" />}
            />
          </MenuItem>
        )}
      </Menu>

    </>
  )
}

export default UserMenu
